import { useState } from 'react';
import { MagnifyingGlass, CloudArrowDown, CaretRight } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import api from '../lib/api';

/**
 * Golf API import search box — shared between OwnerCoursesList and OwnerAddCourse.
 *
 * Props:
 *   onImport(course): called with the mapped course payload when user selects an API result
 *   onLocalEdit(id): called when user taps a local match to edit it
 *   initialQuery: optional pre-filled search term
 */
export default function GolfApiImport({ onImport, onLocalEdit, initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(null); // courseID being loaded
  const [error, setError] = useState('');
  const [showApiResults, setShowApiResults] = useState(false);

  const handleSearch = async () => {
    const q = query.trim();
    if (q.length < 2) return;
    setSearching(true);
    setError('');
    setResults(null);
    setShowApiResults(false);
    try {
      const { data } = await api.get(`/owner/golf-api/search?q=${encodeURIComponent(q)}`);
      setResults(data);
      if (!data.local_matches?.length) setShowApiResults(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Search failed');
    } finally { setSearching(false); }
  };

  const handleSelectApiResult = async (courseId) => {
    setLoading(courseId);
    setError('');
    try {
      const { data } = await api.get(`/owner/golf-api/course/${courseId}`);
      if (data.already_imported) {
        setError('Already imported');
        setLoading(null);
        return;
      }
      // Thread the external API course ID through so Add Course can save it
      onImport({ ...data, api_course_id: courseId });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Failed to fetch course');
    } finally { setLoading(null); }
  };

  const hasLocalMatches = results?.local_matches?.length > 0;
  const hasApiResults = results?.api_results?.length > 0;

  return (
    <Card className="p-4 bg-[#F4F6F5] border-[#E1E6E4] rounded-xl space-y-3" data-testid="golf-api-import">
      <div className="flex items-center gap-2">
        <CloudArrowDown className="w-5 h-5 text-[#1B4332]" weight="bold" />
        <span className="text-sm font-bold text-[#1D242B]">Import from Golf API</span>
      </div>
      <div className="flex gap-2">
        <Input value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
          placeholder="Search course or club name..."
          className="h-10 rounded-lg text-sm flex-1" data-testid="golf-api-search-input" />
        <Button onClick={handleSearch} disabled={searching || query.trim().length < 2}
          className="h-10 px-4 bg-[#1B4332] text-white rounded-lg text-sm" data-testid="golf-api-search-btn">
          {searching ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" /> : <MagnifyingGlass className="w-4 h-4" weight="bold" />}
        </Button>
      </div>

      {error && <p className="text-xs text-[#D32F2F]" data-testid="golf-api-error">{error}</p>}

      {results && (
        <div className="space-y-2" data-testid="golf-api-results">
          {/* Local matches */}
          {hasLocalMatches && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold text-[#455561] uppercase tracking-wide">Already in Fore Tour</p>
              {results.local_matches.map(m => (
                <button key={m._id || m.id} onClick={() => onLocalEdit?.(m._id || m.id)}
                  className="w-full text-left px-3 py-2 bg-white rounded-lg border border-[#E1E6E4] hover:border-[#1B4332] transition-colors"
                  data-testid={`local-match-${m._id || m.id}`}>
                  <p className="text-sm font-semibold text-[#1D242B]">{m.name}</p>
                  <p className="text-[10px] text-[#455561]">{m.region} · {m.tee_count} tee{m.tee_count !== 1 ? 's' : ''}</p>
                </button>
              ))}
              {hasApiResults && !showApiResults && (
                <button onClick={() => setShowApiResults(true)}
                  className="text-xs text-[#1B4332] font-semibold hover:underline" data-testid="golf-api-show-anyway">
                  Import from API anyway →
                </button>
              )}
            </div>
          )}

          {/* API results */}
          {showApiResults && hasApiResults && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold text-[#455561] uppercase tracking-wide">Golf API results</p>
              {results.api_results.map(r => (
                <button key={r.id} onClick={() => !r.already_imported && handleSelectApiResult(r.id)}
                  disabled={!!loading || r.already_imported}
                  className={`w-full text-left px-3 py-2 bg-white rounded-lg border border-[#E1E6E4] hover:border-[#1B4332] transition-colors flex items-center ${loading || r.already_imported ? 'opacity-60 cursor-not-allowed' : ''}`}
                  data-testid={`api-result-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1D242B] truncate">{r.name}</p>
                    <p className="text-[10px] text-[#455561] truncate">{r.club} · {r.location}</p>
                  </div>
                  {r.already_imported
                    ? <span className="text-[10px] text-[#2E7D32] font-semibold ml-2 flex-shrink-0">Already imported</span>
                    : loading === r.id
                      ? <span className="text-[10px] text-[#1B4332] font-semibold ml-2 flex-shrink-0">Importing…</span>
                      : <CaretRight className="w-4 h-4 text-[#B8C7C0] ml-2 flex-shrink-0" />}
                </button>
              ))}
            </div>
          )}

          {!hasLocalMatches && !hasApiResults && (
            <p className="text-xs text-[#455561] text-center py-2">No courses found</p>
          )}
        </div>
      )}
    </Card>
  );
}
